import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Search,
  Refresh,
  HourglassEmpty,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import type { IdentityVerification, IdentityStatus } from "./types";
import { listIdentityVerifications, DOC_TYPE_LABELS } from "./identityVerificationApi";
import VerificationDetailModal from "./VerificationDetailModal";

const TEAL = "#079889";

const STATUS_CONFIG: Record<
  IdentityStatus,
  { label: string; bg: string; color: string; icon: React.ReactElement }
> = {
  not_submitted: {
    label: "Not Submitted",
    bg: "#f1f5f9",
    color: "#475569",
    icon: <HourglassEmpty sx={{ fontSize: 14 }} />,
  },
  pending: {
    label: "Pending",
    bg: "#fef3c7",
    color: "#92400e",
    icon: <HourglassEmpty sx={{ fontSize: 14 }} />,
  },
  approved: {
    label: "Approved",
    bg: "#d1fae5",
    color: "#065f46",
    icon: <CheckCircle sx={{ fontSize: 14 }} />,
  },
  rejected: {
    label: "Rejected",
    bg: "#fee2e2",
    color: "#991b1b",
    icon: <Cancel sx={{ fontSize: 14 }} />,
  },
};

const FILTER_TABS: { label: string; value: string }[] = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

interface Props {
  onPendingCount?: (count: number) => void;
}

export default function VerificationQueue({ onPendingCount }: Props = {}) {
  const [records, setRecords] = useState<IdentityVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState<IdentityVerification | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const status = filterStatus === "all" ? undefined : filterStatus;
      const data = await listIdentityVerifications(status);
      const filteredData = data.filter((r) => r.status !== "not_submitted");
      setRecords(filteredData);
      // Notify parent with the total pending count (always from the full set)
      if (onPendingCount) {
        if (filterStatus === "all" || filterStatus === "pending") {
          onPendingCount(filteredData.filter((r) => r.status === "pending").length);
        }
      }
    } catch (err) {
      console.error("Failed to fetch verifications:", err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, onPendingCount]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Reset page when filter/search changes
  useEffect(() => {
    setPage(0);
  }, [filterStatus, search]);

  const filtered = useMemo(() => {
    if (!search.trim()) return records;
    const q = search.toLowerCase();
    return records.filter(
      (r) =>
        r.user_full_name?.toLowerCase().includes(q) ||
        r.user_email?.toLowerCase().includes(q),
    );
  }, [records, search]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const handleRowClick = (record: IdentityVerification) => {
    setSelected(record);
    setModalOpen(true);
  };

  const handleUpdated = (updated: IdentityVerification) => {
    setRecords((prev) => {
      const next = prev.map((r) => (r.id === updated.id ? updated : r));
      if (onPendingCount) {
        onPendingCount(next.filter((r) => r.status === "pending").length);
      }
      return next;
    });
  };

  const pendingCount = records.filter((r) => r.status === "pending").length;

  return (
    <>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h6" fontWeight="bold">
                Document Verification Queue
              </Typography>
              {pendingCount > 0 && (
                <Chip
                  label={`${pendingCount} pending`}
                  size="small"
                  sx={{
                    bgcolor: "#fef3c7",
                    color: "#92400e",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" mt={0.25}>
              Review and verify fundraiser identity documents
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "text.secondary", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                minWidth: 260,
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={fetchRecords}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <Refresh fontSize="small" />
                )
              }
              sx={{
                textTransform: "none",
                borderRadius: 2,
                borderColor: "divider",
                color: "text.primary",
                whiteSpace: "nowrap",
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Filter tabs */}
        <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
          {FILTER_TABS.map((tab) => (
            <Button
              key={tab.value}
              size="small"
              variant={filterStatus === tab.value ? "contained" : "outlined"}
              onClick={() => setFilterStatus(tab.value)}
              sx={{
                textTransform: "none",
                borderRadius: 4,
                px: 2,
                fontSize: "0.8rem",
                ...(filterStatus === tab.value
                  ? {
                    bgcolor: TEAL,
                    "&:hover": { bgcolor: "#057a6e" },
                    borderColor: TEAL,
                  }
                  : {
                    borderColor: "divider",
                    color: "text.secondary",
                  }),
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>

        {/* Table */}
        {loading && records.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress size={28} sx={{ color: TEAL }} />
          </Box>
        ) : (
          <Table sx={{ "& td, & th": { borderBottom: "none" } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Document Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Submitted</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {search
                        ? "No results match your search"
                        : `No ${filterStatus === "all" ? "" : filterStatus + " "}verifications found`}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((record) => {
                  const cfg =
                    STATUS_CONFIG[record.status];
                  return (
                    <TableRow
                      key={record.id}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:hover": { bgcolor: "#f9fafb" },
                      }}
                      onClick={() => handleRowClick(record)}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        {record.user_full_name || "—"}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {record.user_email}
                      </TableCell>
                      <TableCell>
                        {record.id_document_type
                          ? DOC_TYPE_LABELS[record.id_document_type] ??
                          record.id_document_type
                          : "—"}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {record.submitted_at
                          ? new Date(record.submitted_at).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            },
                          )
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={cfg.icon}
                          label={cfg.label}
                          size="small"
                          sx={{
                            bgcolor: cfg.bg,
                            color: cfg.color,
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            "& .MuiChip-icon": { color: cfg.color },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(record);
                          }}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            fontSize: "0.75rem",
                            ...(record.status === "pending"
                              ? {
                                borderColor: TEAL,
                                color: TEAL,
                                "&:hover": {
                                  bgcolor: "#e6f7f5",
                                  borderColor: TEAL,
                                },
                              }
                              : {
                                borderColor: "divider",
                                color: "text.secondary",
                              }),
                          }}
                        >
                          {record.status === "pending" ? "Review" : "View"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      <VerificationDetailModal
        record={selected}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onUpdated={handleUpdated}
      />
    </>
  );
}
